"use client";

import { memo, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Typography } from "@/components/Typography";
import { cn, formatBudget } from "@/lib/utils";
import { AttributeIcon } from "@/components/main-services/hotels/detail/rooms/AttributeIcon";
import { useHotelFilterCriteria } from "@/hooks/hotel/useHotel";
import {
  IconCalendar,
  IconCurrencyDollar,
  IconEdit,
  IconFileText,
  IconHeart,
  IconMapPin,
  IconUsers,
} from "@tabler/icons-react";
import {
  BookForMeFormData,
  extractFacilityAttributes,
} from "../book-for-me.type";

interface BookForMeRequestSummaryProps {
  data: BookForMeFormData;
  className?: string;
}

export const BookForMeRequestSummary = memo(function BookForMeRequestSummary({
  data,
  className,
}: BookForMeRequestSummaryProps) {
  const { data: criteriaResponse } = useHotelFilterCriteria();
  const facilities = useMemo(
    () => extractFacilityAttributes(criteriaResponse?.data),
    [criteriaResponse?.data],
  );
  return (
    <Card
      className={cn(
        "rounded-3xl border bg-white p-4 md:p-6 shadow-product-card space-y-4",
        className,
      )}
    >
      {/* Header following StepHeader style */}
      <div className="flex items-start gap-3">
        <div className="bg-primary-muted grid size-10 shrink-0 items-center justify-center rounded-full text-primary">
          <IconFileText className="size-5" stroke={2} />
        </div>

        <div className="space-y-0.5">
          <Typography variant="subtitle1" mdVariant="h6">
            Your request summary
          </Typography>
          <Typography variant="body2" className="text-body">
            Here&apos;s the information you submitted.
          </Typography>
        </div>
      </div>

      {/* Details Grid */}
      <div className="grid gap-4 grid-cols-2">
        {(
          [
            [IconMapPin, "Destination", data.destination || "Siem Reap"],
            [IconCalendar, "Dates", data.date || "Flexible dates"],
            [IconUsers, "Guests", data.guests || "2 adults · 1 child · 1 room"],
            [
              IconCurrencyDollar,
              "Budget",
              formatBudget(data.budgetMin, data.budgetMax),
            ],
          ] as const
        ).map(([Icon, label, value]) => (
          <div key={label} className="flex items-start gap-3">
            <div className="bg-surface-sunken grid size-10 shrink-0 items-center justify-center rounded-full text-body">
              <Icon className="size-5" />
            </div>
            <div className="min-w-0 space-y-0.5">
              <Typography variant="body2" className="text-body">
                {label}
              </Typography>
              <Typography variant="body1" className="line-clamp-2">
                {value}
              </Typography>
            </div>
          </div>
        ))}
      </div>

      {/* Preferences */}
      <div className="flex items-start gap-3 pt-1">
        <div className="bg-surface-sunken grid size-10 shrink-0 items-center justify-center rounded-full text-body">
          <IconHeart className="size-5" />
        </div>
        <div className="space-y-2 flex-1 min-w-0">
          <Typography variant="button-sm" className="text-caption">
            Your preferences
          </Typography>
          <div className="flex flex-wrap gap-2.5">
            {facilities.map((pref) => {
              const prefId = String(pref.id);
              const isChecked = data.preferences.includes(prefId);
              return (
                <div
                  key={prefId}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-xs font-medium transition-all md:text-sm",
                    isChecked
                      ? "bg-primary text-body border-primary"
                      : "bg-white text-body border-border",
                  )}
                >
                  <AttributeIcon
                    attribute={pref as never}
                    className="size-4 text-body"
                  />
                  <span>{pref.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Additional requests */}
      <div className="flex items-start gap-3 pt-1">
        <div className="bg-surface-sunken grid size-10 shrink-0 items-center justify-center rounded-full text-body">
          <IconEdit className="size-5" />
        </div>
        <div className="space-y-0.5 flex-1 min-w-0">
          <Typography variant="button-sm" className="text-caption">
            Additional requests
          </Typography>
          <Typography variant="body2">
            {data.additionalRequests?.trim() || "None"}
          </Typography>
        </div>
      </div>
    </Card>
  );
});

export default BookForMeRequestSummary;
